import { View, Text, Pressable, Image, Platform } from "react-native";
import { ChevronRight } from "lucide-react-native";
import { GLASS_CARD_STYLE, colors } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type Props = {
  /** require()'d image source. Typically a 3D 44x44 icon. */
  icon: number;
  title: string;
  subtitle: string;
  /** When provided, the card becomes pressable and shows a trailing chevron. */
  onPress?: () => void;
  /** Stable analytics identifier; the card's title travels as the label. */
  action?: string;
};

export function DialogInfoCard({
  icon,
  title,
  subtitle,
  onPress,
  action,
}: Props) {
  const capturePress = usePressCapture("DialogInfoCard");
  const baseStyle = {
    ...GLASS_CARD_STYLE,
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 14,
    padding: 14,
  };

  const inner = (
    <>
      <Image
        source={icon}
        style={{
          width: 44,
          height: 44,
          ...(Platform.OS === "web" && { mixBlendMode: "overlay" }),
        }}
        resizeMode="contain"
      />
      <View style={{ flex: 1, gap: 2 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            fontWeight: "600",
            color: colors.text,
            lineHeight: 19,
          }}
        >
          {title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            color: colors.textLight,
            lineHeight: 17,
          }}
        >
          {subtitle}
        </Text>
      </View>
      {onPress && (
        <ChevronRight size={16} color={colors.textLight} strokeWidth={1.5} />
      )}
    </>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={() => {
          capturePress(pressLabel(title), action);
          onPress();
        }}
        style={baseStyle}
      >
        {inner}
      </Pressable>
    );
  }
  return <View style={baseStyle}>{inner}</View>;
}
